import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui';
import { adminIntegrationsQuery, variantSearchQuery } from '@/queries';

/**
 * The Shopify bridge, as a screen. T112, T113.
 *
 * Three questions, in the order somebody asks them:
 *
 *   1. **Is it on?** A bridge that is quietly off looks exactly like a shop
 *      with no orders, and the second one is a much better day.
 *   2. **What needs me?** The exception queue. An order Shopify took that this
 *      system would not guess at. Each one names the line it could not match
 *      and offers the only action that fixes it.
 *   3. **What has it been doing?** The log, so "it did not come through" has an
 *      answer that is not a shrug.
 *
 * The bridge NEVER guesses a variant (FR-404, invariant B-2). That rule is the
 * reason this screen exists: guessing by title is how a customer receives a
 * large black hoodie because the medium navy sold out and the titles were
 * similar.
 */
export default function AdminIntegrationsPage() {
  const qc = useQueryClient();
  const state = useSuspenseQuery(adminIntegrationsQuery).data;

  return (
    <section className="p-6">
      <p className="font-[family-name:var(--font-mono)] text-[11px] uppercase tracking-[0.08em] text-muted">
        Commerce
      </p>
      <h1 className="mt-1 text-[26px] font-bold">Shopify</h1>

      <p className="mt-2 text-[14px] text-muted">
        {state.connected ? (
          <>
            Listening for webhooks
            {state.shop ? <> from <span className="font-medium">{state.shop}</span></> : null}
            {' · '}
            {state.variants_mapped} variant{state.variants_mapped === 1 ? '' : 's'} mapped
          </>
        ) : (
          <>
            <strong>Off.</strong> No webhook secret is configured, so nothing from Shopify can be
            verified and nothing is accepted. That is deliberate: an endpoint that skips the
            check when it is not configured is an endpoint that creates orders for anybody.
          </>
        )}
      </p>

      <h2 className="mt-8 text-[18px] font-bold">
        Waiting for somebody {state.exceptions.length > 0 && (
          <Badge tone="warn">{state.exceptions.length}</Badge>
        )}
      </h2>
      {state.exceptions.length === 0 ? (
        <p className="mt-2 rounded-[10px] border border-line bg-panel p-6 text-center text-[14px] text-muted">
          Nothing set aside. Every order Shopify sent has been matched and created.
        </p>
      ) : (
        <div className="mt-3 grid gap-3">
          {state.exceptions.map((e) => (
            <Exception key={e.id} exception={e} onDone={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'integrations'] });
            }} />
          ))}
        </div>
      )}

      {state.waiting.length > 0 && (
        <>
          <h2 className="mt-8 text-[18px] font-bold">Still in the queue</h2>
          <div className="mt-3 rounded-[10px] border border-line bg-panel">
            {state.waiting.map((w) => (
              <div key={w.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3 last:border-0">
                <span className="flex flex-col">
                  <span className="font-[family-name:var(--font-mono)] text-[13px]">{w.topic}</span>
                  <span className="text-[12px] text-muted">
                    {new Date(w.received_at).toLocaleString('en-AU')} · attempt {w.attempts}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  {w.gave_up && <Badge tone="warn">gave up</Badge>}
                  {w.late && !w.gave_up && <Badge tone="warn">over an hour</Badge>}
                  {w.error && <span className="text-[13px] text-muted">{w.error}</span>}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <h2 className="mt-8 text-[18px] font-bold">What it has been doing</h2>
      {state.log.length === 0 ? (
        <p className="mt-2 text-[14px] text-muted">Nothing yet.</p>
      ) : (
        <div className="mt-3 rounded-[10px] border border-line bg-panel">
          {state.log.map((line) => (
            <div key={line.id} className="flex flex-wrap items-baseline justify-between gap-3 border-b border-line px-4 py-2.5 last:border-0">
              <span className="flex items-baseline gap-2">
                <Badge tone={line.status === 'ok' ? 'ok' : 'warn'}>{line.status}</Badge>
                <span className="font-[family-name:var(--font-mono)] text-[12px] text-muted">
                  {line.direction} · {line.entity}
                </span>
                <span className="text-[13px]">{describe(line.detail)}</span>
              </span>
              <span className="font-[family-name:var(--font-mono)] text-[11px] tabular-nums text-faint">
                {new Date(line.at).toLocaleString('en-AU')}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** The log stores jsonb. This turns it into the one sentence worth reading. */
function describe(detail: Record<string, unknown> | null): string {
  if (!detail) return '';
  const said = detail.said ?? detail.why ?? detail.topic ?? '';
  return typeof said === 'string' ? said : JSON.stringify(said);
}

/**
 * Which of our variants is this Shopify line.
 *
 * This was a `<select>` holding every active variant in the catalogue: 3,338
 * options, built from a read of every product with every variant embedded.
 * Nobody scrolls 3,338 options, and the read behind it would have been
 * truncated at 1000 by PostgREST without a word. So the person types and the
 * database searches, and at most 25 come back. R-231.
 */
function VariantPicker({ value, onPick }: { value: string; onPick: (id: string) => void }) {
  const [term, setTerm] = useState('');
  const [needle, setNeedle] = useState('');
  const [label, setLabel] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setNeedle(term), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isFetching } = useQuery(variantSearchQuery(needle));
  const rows = data?.rows ?? [];

  if (value) {
    return (
      <span className="flex min-w-[280px] items-center gap-2 text-[14px]">
        <span className="font-semibold">{label || 'Chosen'}</span>
        <button
          type="button"
          className="text-[13px] text-muted underline"
          onClick={() => { onPick(''); setLabel(''); setTerm(''); }}
        >
          change
        </button>
      </span>
    );
  }

  return (
    <span className="relative min-w-[280px]">
      <input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Type a name, colour, size or SKU"
        aria-label="Search for the matching variant"
        className="h-10 w-full rounded-[10px] border border-line bg-panel px-2 text-[14px]"
      />
      {needle.length >= 2 && (
        <span className="absolute left-0 top-[42px] z-20 block max-h-[240px] w-full overflow-y-auto
                         rounded-[10px] border border-line-strong bg-panel shadow-[var(--shadow-2)]">
          {isFetching && rows.length === 0 && (
            <span className="block px-3 py-2 text-[13px] text-muted">Searching...</span>
          )}
          {!isFetching && rows.length === 0 && (
            <span className="block px-3 py-2 text-[13px] text-muted">
              Nothing matches that. Try the SKU.
            </span>
          )}
          {rows.map((v) => (
            <button
              key={v.id}
              type="button"
              className="block w-full px-3 py-2 text-left text-[13px] hover:bg-raised"
              onClick={() => { onPick(v.id); setLabel(v.label); }}
            >
              {v.label}
            </button>
          ))}
          {rows.length === 25 && (
            <span className="block border-t border-line px-3 py-2 text-[12px] text-muted">
              The first 25. Type more to narrow it.
            </span>
          )}
        </span>
      )}
    </span>
  );
}

/**
 * One order Shopify took that nothing here could match.
 *
 * The action is the whole point: choose the variant once, and this order and
 * every future order of it go through. Mapping is remembered; guessing is not
 * offered.
 */
function Exception({ exception, onDone }: {
  exception: {
    id: string; topic: string; received_at: string; error: string;
    lines: { shopify_variant_id: string; title: string; qty: number }[];
  };
  onDone: () => void;
}) {
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const map = useMutation({
    mutationFn: async (shopifyVariantId: string) => {
      const res = await fetch('/api/integrations/map-variant', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          shopify_variant_id: shopifyVariantId,
          variant_id: choice[shopifyVariantId],
          retry: exception.id,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return res.json() as Promise<{ requeued: number }>;
    },
    onSuccess: (r) => {
      setError(null);
      setDone(
        r.requeued > 0
          ? 'Mapped, and the order is back in the queue. It will come through within a minute.'
          : 'Mapped.',
      );
      onDone();
    },
    onError: (e: Error) => { setDone(null); setError(e.message); },
  });

  return (
    <div className="rounded-[10px] border border-line bg-panel p-4">
      <p className="text-[13px] text-muted">
        {exception.topic} · {new Date(exception.received_at).toLocaleString('en-AU')}
      </p>
      <p className="mt-1 text-[14px]">{exception.error}</p>

      <div className="mt-3 grid gap-2">
        {exception.lines.map((l) => (
          <div key={l.shopify_variant_id} className="flex flex-wrap items-center gap-2">
            <span className="min-w-[220px] text-[14px] font-semibold">
              {l.title} <span className="font-normal text-muted">× {l.qty}</span>
            </span>
            <VariantPicker
              value={choice[l.shopify_variant_id] ?? ''}
              onPick={(id) => setChoice((c) => ({ ...c, [l.shopify_variant_id]: id }))}
            />
            <button
              type="button"
              disabled={!choice[l.shopify_variant_id] || map.isPending}
              onClick={() => map.mutate(l.shopify_variant_id)}
              className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold disabled:opacity-50"
            >
              {map.isPending ? 'Mapping…' : 'Map it and retry'}
            </button>
          </div>
        ))}
      </div>

      {error && <p className="mt-3 rounded-[10px] border border-line p-3 text-[14px]">{error}</p>}
      {done && <p className="mt-3 text-[14px] text-muted">{done}</p>}
    </div>
  );
}
